import http from 'api/httpClient';
import { CustomerDataResponse, BranchDataResponse } from '@/pages/customer-data/api';
import { BranchwiseUnbilledRow, BranchCnDetailsRow } from '@/pages/unbilled/api/api';
import { AraUserTableRow, BillDetailRow, DueStatus } from '@/pages/ara/api';

// ---------- shared ----------------------------------------------------------
export interface SnapshotRef {
  year: string;
  month: number;
  day: number;
}

interface Paginated {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  has_previous: boolean;
  has_next: boolean;
}

// ---------- landing ---------------------------------------------------------
export type LandingSortKey =
  | 'total_exposure'
  | 'revenue_cy'
  | 'revenue_py'
  | 'revenue_growth_pct'
  | 'cm_cy'
  | 'cm_pct_cy'
  | 'overdue_gt_30'
  | 'dso'
  | 'unbilled_excl_stock'
  | 'net_outstanding'
  | 'active_branches'
  | 'customer_name';

export interface KeyAccountCardData {
  customer_code: string;
  customer_name: string;
  revenue_py: number;
  revenue_cy: number;
  revenue_growth_amt: number;
  revenue_growth_pct: number | null;
  cm_cy: number;
  cm_py: number;
  cm_growth_amt: number;
  cm_growth_pct: number | null;
  cm_pct_cy: number;
  cm_pct_py: number;
  cm_pct_delta: number | null;
  overdue_gt_30: number;
  overdue_gt_30_delta: number | null;
  unbilled_excl_stock: number;
  unbilled_excl_stock_delta: number | null;
  dso: number | null;
  net_outstanding: number;
  total_exposure: number;
  active_branches: number;
  sparkline: number[];
}

export interface KeyAccountsPortfolio {
  customer_count: number;
  revenue_py: number;
  revenue_cy: number;
  revenue_growth_amt: number;
  revenue_growth_pct: number | null;
  cm_cy: number;
  cm_py: number;
  cm_growth_amt: number;
  cm_growth_pct: number | null;
  cm_pct_cy: number;
  cm_pct_py: number;
  cm_pct_delta: number | null;
  overdue_gt_30: number;
  overdue_gt_30_delta: number | null;
  unbilled_excl_stock: number;
  unbilled_excl_stock_delta: number | null;
  dso: number | null;
  net_outstanding: number;
  total_exposure: number;
}

export interface KeyAccountsLandingResponse {
  total: number;
  division_ou_code: string;
  division_name: string;
  snapshots: {
    unbilled: SnapshotRef | null;
    outstanding: SnapshotRef | null;
    prev_unbilled: SnapshotRef | null;
    prev_outstanding: SnapshotRef | null;
  };
  portfolio: KeyAccountsPortfolio;
  items: KeyAccountCardData[];
}

export async function fetchKeyAccountsLanding(params: {
  year: string;
  search?: string;
  sort: { by: LandingSortKey; dir: 'asc' | 'desc' };
  page: number;
  page_size: number;
}): Promise<KeyAccountsLandingResponse> {
  return http.post('/api/v1/key-accounts/landing', params);
}

// ---------- account header --------------------------------------------------
export interface KeyAccountHeader {
  customer_code: string;
  customer_name: string;
  division_ou_code: string;
  division_name: string;
}

export async function fetchKeyAccountHeader(customerCode: string): Promise<KeyAccountHeader> {
  return http.get(`/api/v1/key-accounts/accounts/${encodeURIComponent(customerCode)}`);
}

// ---------- revenue & profitability tab -------------------------------------
export async function fetchKeyAccountProfitability(params: {
  customer_code: string;
  year: string;
  filters: Record<string, string[]>;
}): Promise<CustomerDataResponse> {
  return http.post('/api/v1/key-accounts/profitability', params);
}

export async function fetchKeyAccountProfitabilityBranches(params: {
  customer_code: string;
  year: string;
  filters: Record<string, string[]>;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
}): Promise<BranchDataResponse> {
  return http.post('/api/v1/key-accounts/profitability/branches', params);
}

// ---------- unbilled tab ----------------------------------------------------
export interface KeyAccountUnbilledSummaryResponse extends Paginated {
  snapshot: SnapshotRef | null;
  // rows[0] is the customer TOTAL row (same convention as the unbilled report)
  rows: BranchwiseUnbilledRow[];
}

export async function fetchKeyAccountUnbilledSummary(params: {
  customer_code: string;
  search?: string;
  page?: number;
  per_page?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}): Promise<KeyAccountUnbilledSummaryResponse> {
  return http.post('/api/v1/key-accounts/unbilled/summary', params);
}

export interface KeyAccountCnDetailsResponse extends Paginated {
  snapshot: SnapshotRef | null;
  rows: BranchCnDetailsRow[];
}

export async function fetchKeyAccountCnDetails(params: {
  customer_code: string;
  ou_code: string;
  status?: string;
  search?: string;
  page?: number;
  per_page?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}): Promise<KeyAccountCnDetailsResponse> {
  return http.post('/api/v1/key-accounts/unbilled/cn-details', params);
}

export async function downloadKeyAccountCnDetails(params: {
  customer_code: string;
  ou_code: string;
  status?: string;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}): Promise<Blob> {
  return http.post('/api/v1/key-accounts/unbilled/cn-details/download', params, {
    responseType: 'blob',
  });
}

// ---------- accounts receivable tab -----------------------------------------
export interface KeyAccountArSummary {
  credit_period: number | null;
  total_outstanding: number;
  not_due: number;
  total_overdue: number;
  bucket_1_30: number;
  bucket_31_60: number;
  bucket_61_90: number;
  bucket_91_120: number;
  bucket_121_180: number;
  bucket_over_180: number;
  legal: number;
  unapplied_receipts: number;
  net_outstanding: number;
  dso: number | null;
}

export interface KeyAccountArSummaryResponse {
  snapshot: SnapshotRef | null;
  summary: KeyAccountArSummary;
}

export async function fetchKeyAccountArSummary(params: {
  customer_code: string;
}): Promise<KeyAccountArSummaryResponse> {
  return http.post('/api/v1/key-accounts/receivables/summary', params);
}

export interface KeyAccountArByPersonResponse extends Paginated {
  snapshot: SnapshotRef | null;
  // rows[0] is the TOTAL row (same convention as the ARA by-user table)
  rows: AraUserTableRow[];
}

export async function fetchKeyAccountArByPerson(params: {
  customer_code: string;
  page?: number;
  per_page?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}): Promise<KeyAccountArByPersonResponse> {
  return http.post('/api/v1/key-accounts/receivables/by-person', params);
}

export interface KeyAccountBillsResponse extends Paginated {
  snapshot: SnapshotRef | null;
  customer_name: string;
  customer_code: string;
  items: BillDetailRow[];
}

export async function fetchKeyAccountBills(params: {
  customer_code: string;
  ecode?: string;
  search?: string;
  due_status?: DueStatus;
  page?: number;
  per_page?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}): Promise<KeyAccountBillsResponse> {
  return http.post('/api/v1/key-accounts/receivables/bills', params);
}

export async function downloadKeyAccountBills(params: {
  customer_code: string;
  ecode?: string;
  search?: string;
  due_status?: DueStatus;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}): Promise<Blob> {
  return http.post('/api/v1/key-accounts/receivables/bills/download', params, {
    responseType: 'blob',
  });
}

// ---------- dropdowns (revenue & profitability tab filters) ------------------
export const updateKeyAccountsDropdowns = (payload: {
  year: string;
  selection: Record<string, string[]>;
}) => {
  return http.post('/api/v1/reports/key_accounts/update-dropdowns', payload);
};
